import { faAngleRight, faClock, faCommentDots, faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSession } from "next-auth/react";
import Image from "next/image";
import Link from "next/link";
import dynamic from "next/dynamic";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import Kosana from "../kosana/Kosana";
import { formatThaiDate } from "../ui/DateFormat";
import GameCapsuleTag from "../ui/GameCapsuleTag";
import GameDetailCard from "../ui/GameDetailCard";
import GameIconTag from "../ui/GameIconTag";
import ContentBodyHtml from "./ContentBodyHtml";
import BannerAdvertise from "../../assets/images/BannerAdvertise.webp";
import HorizontalAds from "../kosana/HorizontalAds";
import BannerRow from "../kosana/BannerRow";
import RatingStar from "../ui/RatingStar";
import WriterDesctiption from "./WriterDescription";
import { signIn } from "next-auth/react";
import SmallAds from "../kosana/SmallAds";
import VerticalAds from "../kosana/VerticalAds";
import { SITE_URL } from "../../utils/seo";
import ShareDropdown from "../ui/ShareDropdown";
import LikeIconButton from "../ui/LikeIconButton";
import SaveButton from "../ui/SaveButton";
import CopyLinkButton from "../ui/CopyLinkButton";
import AuthorAvatar from "../ui/AuthorAvatar";
import { DEFAULT_AVATAR } from "../../utils/image";

// Lexical bodies (JSON with a "root") still render client-side; HTML bodies are
// server-rendered by ContentBodyHtml (see that file for what changed and why).
const ContentBodyLexical = dynamic(() => import("./ContentBodyLexical"), { ssr: false });
const Comment = dynamic(() => import("./Comment"), { ssr: false });
const RecommendedList = dynamic(() => import("./RecommendedList"));
const LatestGuideList = dynamic(() => import("./latestGuideList"));
const GameGuide = dynamic(() => import("./GameGuide"));
const ImageGallery = dynamic(() => import("./ImageGallery"));

// Thai section names, the same ones the navbar uses.
const CATEGORY_TH: Record<string, string> = {
  News: "ข่าว",
  Review: "รีวิว",
  ReviewBanner: "รีวิว",
  Feature: "บทความ",
  Guide: "ไกด์",
};

// Broadcast clock, as on the home page's lower-third (HeroBanner): 24-hour,
// pinned to Bangkok so SSR (UTC on Workers) and the browser print the same
// time. One formatter, built lazily (see DateFormat).
let clockFormatter: Intl.DateTimeFormat | null = null;
const clock = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  clockFormatter ??= new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Bangkok",
  });
  return clockFormatter.format(d);
};
// "15 ก.ย. 2026 · 14:12"
const stamp = (iso?: string | null) => {
  const day = formatThaiDate(iso || "");
  const time = clock(iso);
  return day && time ? `${day} · ${time}` : day;
};

const CRUMB_LINK = "transition-colors hover:text-signal dark:hover:text-[#8DB4FF]";
// The jump-to-comments plate. LikeIconButton, ShareDropdown and
// CopyLinkButton draw the same 40px plate themselves.
const ACTION_PLATE =
  "flex h-10 w-10 items-center justify-center bg-theme-surface text-theme-text transition-colors duration-150 hover:bg-signal hover:text-white";
// SaveButton's round overlay icon, squared into the same plate. Its saved
// state is the only signal of "saved", so it is keyed on the label SaveButton
// sets ("Remove from saved") rather than on its own colour classes.
const SAVE_PLATE =
  "!h-10 !w-10 !rounded-none !border-0 !bg-theme-surface !text-theme-text hover:!bg-signal hover:!text-white [&[aria-label^=Remove]]:!bg-signal [&[aria-label^=Remove]]:!text-white";

const EMPTY_ARTICLE = {
  category: "",
  game: [],
  title: "",
  description: "",
  thumbnail: "",
  publishDt: "",
  contentBody: "",
  author: [],
  imageGallery: [],
  hashtag: [],
};

const Article = (props: any) => {
  const { data: sesstion } = useSession();
  const [likeCount, setLikeCount] = useState(0);
  const [article, setArticle] = useState<any>(props.initialArticle || EMPTY_ARTICLE);

  const [gameData, setGameData] = useState([]);
  const [contentCount, setContentCount] = useState(false);
  const [ownerLike, setOwnerLike] = useState([]);
  // The session carries the backend user id, so no /auth/me round-trip per button.
  const likedByMe = !!sesstion?.user?._id && ownerLike.includes(sesstion.user._id);
  const [articleLoaded, setArticleLoaded] = useState(!!props.initialArticle);
  // When the page seeds the article from server props, mark it fetched so the
  // client fetch below is skipped (removes a full duplicate content request).
  const fetchedArticleIdRef = useRef<string | null>(
    props.initialArticle ? props.articleId : null
  );
  useEffect(() => {
    if (fetchedArticleIdRef.current === props.articleId) {
      return;
    }
    fetchedArticleIdRef.current = props.articleId;

    // fetch content from article id
    const fetchContent = async () => {
      await fetch(`${process.env.BACKEND_BASE_URL}/contents/${props.articleId}`)
        .then((res) => {
          return res.json();
        })
        .then((data) => {
          if (data.success == true) {
            setArticle(data.data);
            setArticleLoaded(true);
          }
        });
    };
    fetchContent();
  }, [props.articleId]);

  // One view beacon per browser view of an article. Server-side renders no
  // longer count as views, so this is the counter. No body on purpose: a JSON
  // Blob is not CORS-simple and would add a preflight. 404s harmlessly until
  // the backend exposes POST /contents/:id/view.
  useEffect(() => {
    if (!props.articleId) return;
    try {
      if (typeof navigator !== "undefined" && "sendBeacon" in navigator) {
        navigator.sendBeacon(
          `${process.env.BACKEND_BASE_URL}/contents/${props.articleId}/view`
        );
      }
    } catch {
      // Beacon failures must never affect the page.
    }
  }, [props.articleId]);

  useEffect(() => {
    // Articles without a game have nothing to recommend from; without this
    // guard the request went out as gameId=undefined.
    if (!props.gameId) return;
    // fetch content from game id
    const fetchGameContent = async () => {
      await fetch(
        `${process.env.BACKEND_BASE_URL}/contents?category=Review,Guide,Feature,ReviewBanner&status=Published&sortBy=publishDt&gameId=${props.gameId}`
      )
        .then((res) => {
          return res.json();
        })
        .then((data) => {
          const gamesContent: any = [];
          const games = data.data || data;
          if (Array.isArray(games)) {
            games.forEach(
              (game: { thumbnail: string; title: string; _id: string }) => {
                gamesContent.push({
                  thumbnail: game.thumbnail,
                  title: game.title,
                  id: game._id,
                  contentId: props.articleId,
                });
              }
            );
          }
          if (gamesContent.length > 0) {
            gamesContent.forEach(function (item, index, object) {
              if (item.id === gamesContent[index].contentId) {
                object.splice(index, 1);
              }
            });
          }
          setGameData(gamesContent);
        });
    };
    fetchGameContent();
  }, []);

  useEffect(() => {
    // fetch like count content from article id
    const fetchLikeCount = async () => {
      await fetch(
        `${process.env.BACKEND_BASE_URL}/contents/like/${props.articleId}`
      )
        .then((res) => {
          return res.json();
        })
        .then((data) => {
          const ownerData: string[] = [];
          const likeData = Array.isArray(data?.data) ? data.data : [];
          likeData.forEach((item) => {
            ownerData.push(item.ownerId[0]);
          });
          setLikeCount(likeData.length);
          setOwnerLike(ownerData);
        });
    };
    fetchLikeCount();
  }, [contentCount]);

  const [guideCount, setGuideCount] = useState(0);
  const [latestGuide, setLatestGuide] = useState([]);
  useEffect(() => {
    // fetch all guide content from game id
    if (props.gameId) {
      const fetchGuideByGameId = async () => {
        await fetch(
          `${process.env.BACKEND_BASE_URL}/contents?status=Published&category=Guide&gameId=${props.gameId}&sortBy=publishDt`
        )
          .then((res) => {
            return res.json();
          })
          .then((data) => {
            const guideContent = [];
            if (data.success == true) {
              setGuideCount(data.data.length);
              data.data.forEach((guide: { title: string; _id: string; thumbnail?: string }) => {
                guideContent.push({
                  title: guide.title,
                  _id: guide._id,
                  thumbnail: guide.thumbnail,
                  contentId: props.articleId,
                });
              });
            }
            if (guideContent.length > 0) {
              guideContent.forEach((item, index, object) => {
                if (item._id === guideContent[index].contentId) {
                  object.splice(index, 1);
                }
              });
            }
            setLatestGuide(guideContent.slice(0, 10));
          });
      };
      fetchGuideByGameId();
    }
  }, []);

  let ratingScore: any;
  if (article.category === "ReviewBanner") {
    article.category = "Review";
  }
  if (article.category === "Review" || article.category === "ReviewBanner") {
    if (article.game[0]) {
      if (article.game[0].reviewScore) {
        ratingScore =
          (article.game[0].reviewScore.gameplay +
            article.game[0].reviewScore.graphicAndSound +
            article.game[0].reviewScore.performance +
            article.game[0].reviewScore.story) /
          4;
      } else if (article.game[0].newReviewScore) {
        ratingScore = article.game[0].newReviewScore.score;
      }
    }
  }
  const [isLogin, setIslogin] = useState(false);
  useEffect(() => {
    if (sesstion) {
      setIslogin(true);
    } else {
      setIslogin(false);
    }
  }, []);

  const historyTrackedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!sesstion?.user?.accessToken || !props.articleId || !articleLoaded) return;
    if (historyTrackedRef.current === props.articleId) return;
    historyTrackedRef.current = props.articleId;
    fetch(`${process.env.BACKEND_BASE_URL}/users/me/history`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sesstion.user.accessToken}`,
      },
      body: JSON.stringify({
        contentId: props.articleId,
        title: props.title,
        thumbnail: article.thumbnail || '',
        category: article.category || '',
        publishDt: article.publishDt || '',
        description: props.description || '',
      }),
    })
      .then((r) => r.json())
      .then((data) => { if (!data.success) console.error('History tracking failed:', data); })
      .catch((err) => console.error('History POST error:', err));
  }, [sesstion?.user?.accessToken, props.articleId, articleLoaded]);

  const [scrollProgress, setScrollProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const scrolled = el.scrollTop || document.body.scrollTop;
      const total = el.scrollHeight - el.clientHeight;
      setScrollProgress(total > 0 ? (scrolled / total) * 100 : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const readTime = useMemo(() => {
    if (!article.contentBody) return 0;
    const plain = article.contentBody
      .replace(/<[^>]+>/g, " ")
      .replace(/[{}"[\]:,\\]/g, " ");
    const words = plain.trim().split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(words / 200));
  }, [article.contentBody]);

  const [currentIndexDesktop, setCurrentIndexDesktop] = useState(0);
  const [currentIndexLaptop, setCurrentIndexLaptop] = useState(0);
  const [currentIndexMobile, setCurrentIndexMobile] = useState(0);
  // Matches the page's canonical rather than hardcoding the origin, so shares
  // from staging don't silently point at production.
  const shareUrl = `${SITE_URL}/article/${props.articleId}`;
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
    shareUrl
  )}`;
  const twitterShareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
    shareUrl
  )}`;

  // One <Comment> serves every breakpoint (see the wrapper after the three
  // trees). Its fetches start only once the anchor scrolls within 600px of the
  // viewport; the anchor itself is always rendered, so SSR markup is unchanged.
  const commentsRef = useRef<HTMLDivElement | null>(null);
  const [commentsActive, setCommentsActive] = useState(false);
  useEffect(() => {
    if (commentsActive) return;
    const el = commentsRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setCommentsActive(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setCommentsActive(true);
          observer.disconnect();
        }
      },
      { rootMargin: "600px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [commentsActive]);

  const scrollToComments = () => {
    // Start the fetch now rather than when the smooth scroll gets there.
    setCommentsActive(true);
    commentsRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // In-article ads are handled inside ContentBodyHtml and ContentBodyLexical.

  const articleSaveData = {
    id: props.articleId,
    title: props.title,
    thumbnail: article.thumbnail,
    description: props.description,
    publishDt: article.publishDt,
    category: article.category,
    author: article.author?.[0] ? [article.author[0]] : [],
    game: article.game?.[0] ? [{ _id: article.game[0]._id, title: article.game[0].title }] : [],
  };

  const tagsSection = article.hashtag && article.hashtag.length > 0 ? (
    <div>
      <div className="flex flex-wrap items-center gap-1.5">
        <p className="m-0 mr-1 text-sm font-medium text-theme-text-secondary">Tags</p>
        {article.hashtag.map((hashtag: string, index: number) => (
          <Link
            key={index}
            href={`/tag/${encodeURIComponent(hashtag)}`}
            className="inline-flex h-8 items-center bg-theme-surface px-3 font-display text-sm font-semibold text-theme-text transition-colors duration-150 hover:bg-signal hover:text-white">

            {hashtag}

          </Link>
        ))}
      </div>
    </div>
  ) : null;

  // ── On Air: the article header, shared by the three layout trees below ──
  // A quiet breadcrumb, then the lower-third: a dateline plate with the
  // Bangkok timestamp, the title on the plate (white on dark, navy on light,
  // the way HeroBanner sets the on-air story) and the meta strip, which
  // leads with the category tag (above the title it read as a kicker that
  // repeated the title's first word). The description follows as a calm
  // lead in the reading face.
  const categoryLabel = CATEGORY_TH[article.category] || article.category || "";
  const headerAuthor = article.author?.[0];
  const headerAuthorName = headerAuthor?.displayName || "Game's On";
  const headerAuthorHref = headerAuthor?._id
    ? `/tag/${headerAuthor._id}?type=author&title=${encodeURIComponent(headerAuthorName)}`
    : "";
  const viewCount = Number(article.view);
  const hasViewCount = Number.isFinite(viewCount) && viewCount > 0;

  const breadcrumb = (
    <nav aria-label="Breadcrumb" className="mb-3">
      <ol className="flex min-w-0 items-center gap-2 text-[13px] font-medium text-theme-text-secondary">
        <li className="shrink-0">
          <Link href="/" prefetch={false} className={CRUMB_LINK}>หน้าแรก</Link>
        </li>
        {article.category ? (
          <Fragment>
            <li aria-hidden="true" className="shrink-0 text-[10px]">
              <FontAwesomeIcon icon={faAngleRight} />
            </li>
            <li className="shrink-0">
              <Link href={`/${article.category.toLocaleLowerCase()}`} prefetch={false} className={CRUMB_LINK}>
                {categoryLabel}
              </Link>
            </li>
          </Fragment>
        ) : null}
        <li aria-hidden="true" className="shrink-0 text-[10px]">
          <FontAwesomeIcon icon={faAngleRight} />
        </li>
        <li aria-current="page" className="min-w-0 truncate">
          {props.title}
        </li>
      </ol>
    </nav>
  );

  const renderLowerThird = (withActions: boolean) => (
    <div className="min-w-0">
      {article.publishDt ? (
        <div className="tag-plate">
          <time dateTime={article.publishDt} className="tabular-nums">
            {stamp(article.publishDt)}
          </time>
        </div>
      ) : null}
      {/* The page's one H1, on the plate. Kanit stacks Thai tone marks high,
          hence the tall leading. */}
      <h1 className="m-0 break-words bg-plate px-4 py-3 font-display text-[28px] font-extrabold italic leading-[1.45] tracking-tight text-plate-ink md:px-6 md:py-4 md:text-[40px]">
        {props.title}
      </h1>
      <div className={`flex flex-wrap items-center gap-x-4 gap-y-2 bg-theme-card px-4 text-sm text-theme-text-secondary md:px-6 ${withActions ? "py-2" : "py-2.5"}`}>
        {categoryLabel ? <span className="tag-plate [--plate-cut:0px]">{categoryLabel}</span> : null}
        {headerAuthor ? (
          headerAuthorHref ? (
            <Link
              href={headerAuthorHref}
              prefetch={false}
              className="inline-flex min-w-0 items-center gap-2 font-medium text-theme-text transition-colors hover:text-signal dark:hover:text-[#8DB4FF]"
            >
              <span className="relative h-6 w-6 shrink-0 overflow-hidden rounded-full bg-theme-surface">
                <AuthorAvatar src={headerAuthor.pictureProfile || DEFAULT_AVATAR} alt="" sizes="24px" />
              </span>
              <span className="truncate">{headerAuthorName}</span>
            </Link>
          ) : (
            <span className="inline-flex min-w-0 items-center gap-2 font-medium text-theme-text">
              <span className="relative h-6 w-6 shrink-0 overflow-hidden rounded-full bg-theme-surface">
                <AuthorAvatar src={headerAuthor.pictureProfile || DEFAULT_AVATAR} alt="" sizes="24px" />
              </span>
              <span className="truncate">{headerAuthorName}</span>
            </span>
          )
        ) : null}
        {readTime > 0 ? (
          <span className="inline-flex items-center gap-1.5 tabular-nums">
            <FontAwesomeIcon icon={faClock} className="text-xs" />
            {readTime} min read
          </span>
        ) : null}
        {hasViewCount ? (
          <span
            className={`inline-flex items-center gap-1.5 tabular-nums ${withActions ? "" : "ml-auto"}`}
            aria-label={`Views: ${viewCount.toLocaleString("en-US")}`}
          >
            <FontAwesomeIcon icon={faEye} className="text-xs" />
            {viewCount.toLocaleString("en-US")}
          </span>
        ) : null}
        {withActions ? (
          <div className="ml-auto flex items-center gap-1.5">
            {renderHeaderActions("!left-auto right-0")}
          </div>
        ) : null}
      </div>
    </div>
  );

  const lead = props.description ? (
    <p className="m-0 mt-4 max-w-[70ch] break-words text-[17px] leading-[1.7] text-theme-text md:text-[19px]">
      {props.description}
    </p>
  ) : null;

  // Same-size plates: comment, like, share, copy, save. Active states are
  // signal blue (in each button). From md they ride at the right end of the
  // meta strip, which keeps the first body image in the first viewport; on
  // phones they get their own row under the lead.
  const renderHeaderActions = (menuClassName = "") => (
    <Fragment>
      <button
        type="button"
        aria-label="Jump to comments"
        onClick={scrollToComments}
        className={ACTION_PLATE}
      >
        <FontAwesomeIcon icon={faCommentDots} className="text-base" />
      </button>
      <LikeIconButton
        articleId={props.articleId}
        token={sesstion?.user.accessToken}
        likedByMe={likedByMe}
        likeCount={likeCount}
        onChanged={() => setContentCount((prev) => !prev)}
      />
      <ShareDropdown
        facebookUrl={facebookShareUrl}
        twitterUrl={twitterShareUrl}
        menuClassName={menuClassName}
      />
      <CopyLinkButton url={shareUrl} />
      <SaveButton article={articleSaveData} variant="overlay" showAddHint className={SAVE_PLATE} />
    </Fragment>
  );

  const footerActions = (
    <div className="flex items-center justify-start gap-1.5">
      <LikeIconButton
        articleId={props.articleId}
        token={sesstion?.user.accessToken}
        likedByMe={likedByMe}
        likeCount={likeCount}
        onChanged={() => setContentCount((prev) => !prev)}
      />
      <ShareDropdown
        facebookUrl={facebookShareUrl}
        twitterUrl={twitterShareUrl}
      />
      <SaveButton article={articleSaveData} variant="overlay" showAddHint className={SAVE_PLATE} />
    </div>
  );

  // Review score bug, platforms and the game plate, above the body.
  const scoreRow = (
    <Fragment>
      {ratingScore ? (
        <RatingStar
          popularReviewContents={article.game[0]}
          rating={props.rating ? props.rating : ratingScore}
        />
      ) : (
        ""
      )}
      {article.game[0] ? (
        <div className="flex items-center gap-2">
          <GameIconTag
            tag={article.game[0].platform}
            className="my-auto"
          />
        </div>
      ) : (
        ""
      )}
      {article.game[0] ? (
        <GameCapsuleTag
          article={true}
          title={article.game[0].title}
          gameId={article.game[0]._id}
        />
      ) : (
        ""
      )}
    </Fragment>
  );

  const writerSection = article.author[0] ? (
    <WriterDesctiption
      writerData={article.author}
    ></WriterDesctiption>
  ) : null;

  const signInPanel = (
    <div className="bg-theme-card text-theme-text">
      <p className="m-0 bg-stage-deep px-4 py-2.5 font-display text-lg font-extrabold italic leading-normal text-white">{`Game's On`}</p>
      <div className="flex flex-col gap-4 p-4">
        <p className="m-0 text-[15px] leading-relaxed text-theme-text-secondary">{`The best community experience, with best support for creator`}</p>
        <button type="button" onClick={() => signIn()} className="btn-signal self-start">
          Sign In
        </button>
      </div>
    </div>
  );

  const guidesPanelTitle = (
    <h2 className="m-0 bg-stage-deep px-4 py-2.5 font-display text-lg font-extrabold italic leading-normal text-white">
      {latestGuide.length === 1 ? "Latest guide" : "Latest guides"}
    </h2>
  );

  return (
    <Fragment>
      {/* Reading progress bar */}
      <div
        className="fixed top-0 left-0 z-[9999] h-[3px] bg-signal transition-none"
        style={{ width: `${scrollProgress}%` }}
        aria-hidden="true"
      />
      {/* desktop */}
      <div className="container mx-auto lg:flex lg:flex-col xl:px-24 lg:px-12 hidden">
        <div className="mt-8 flex mb-8">
          <div className="flex-1 min-w-0 flex-col border-b border-theme-border pb-6">
            {breadcrumb}
            <div className="w-7/10 mb-5">
              {renderLowerThird(true)}
              {lead}
            </div>
            {/* The slot reserves its height itself (HorizontalAds), so no
                `empty:hidden` here: an empty-then-filled wrapper was the
                article page's largest layout shift. */}
            <BannerRow
              initialBannerAd={props.initialTopBannerAd}
              initialSquareAd={props.initialTopSquareAd}
              sideClassName="flex w-3/10 justify-center"
              stackBelow={false}
            />
          </div>
        </div>
        <div className="flex flex-col gap-6">
          <div className="flex gap-10">
            <div className="w-7/10">
              <div className="flex flex-wrap gap-3 mb-6 items-center">
                {scoreRow}
              </div>

              <div className="flex flex-col">
                <div className="w-full flex flex-col gap-6">
                  <div
                    className={`article-body w-full ${
                      article.contentBody.includes("root") ? "editor-shell" : ""
                    }`}
                  >
                    {article.contentBody.includes("root") ? (
                      <ContentBodyLexical contentBody={article.contentBody} />
                    ) : article.contentBody ? (
                      <ContentBodyHtml contentBody={article.contentBody} imageDimensions={props.bodyImageDimensions} />
                    ) : null}
                  </div>

                  {article.imageGallery && article.imageGallery.length > 0 ? (
                    <ImageGallery
                      imageData={article.imageGallery}
                      currentIndex={currentIndexDesktop}
                      setCurrentIndex={setCurrentIndexDesktop}
                      type="Desktop"
                    ></ImageGallery>
                  ) : (
                    ""
                  )}
                  {footerActions}
                  {tagsSection}
                  <div className="w-full max-w-[810px] mx-auto cursor-pointer empty:hidden my-2">
                    <HorizontalAds type="GLOBAL" subLocation="BOTTOM" initialAd={props.initialBottomBannerAd} />
                  </div>
                  {article.game.length > 0 ? (
                    <div className="mb-6">
                      <GameDetailCard
                        id={article.game[0]._id}
                        category={article.category}
                        initialGame={article.game[0]}
                      />
                    </div>
                  ) : (
                    ""
                  )}
                  {writerSection}
                  {/* <div className="w-full max-w-[810px] h-full cursor-pointer">
                    <HorizontalAds
                      type="ARTICLE"
                      subLocation="BOTTOM"
                      mock="youtube"
                    />
                  </div> */}
                  {gameData.length > 2 && article.game.length > 0 ? (
                    <div className="mb-6">
                      <RecommendedList gameContents={gameData} />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-3 w-3/10">
              {isLogin ? null : (
                <div className="md:block hidden">
                  <div className="max-w-[300px] mx-auto">
                    {signInPanel}
                  </div>
                </div>
              )}
              <div className="w-full flex-1 flex flex-col gap-5 justify-between">
                <div className="flex flex-col gap-4">
                     <VerticalAds type="GLOBAL" subLocation="TOP"></VerticalAds>
                  {latestGuide.length > 0 ? (
                    <div className="bg-theme-card">
                      {guidesPanelTitle}
                      {article.game[0] ? (
                        <div>
                          <GameGuide
                            gameId={article.game[0]._id}
                            game={article.game[0]}
                            guideCount={guideCount}
                          />
                        </div>
                      ) : null}
                      <div className="flex flex-col">
                        <LatestGuideList guideList={latestGuide} />
                      </div>
                    </div>
                  ) : null}
                </div>
                <div className="">
                  {/* <SmallAds /> */}
                  <Kosana
                    type="ARTICLE"
                    subLocation="SIDEBOTTOM"
                    ads="double"
                  ></Kosana>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* laptop */}
      <div className="md:flex md:flex-col hidden lg:hidden px-8">
        <div className="mt-8 flex mb-8">
          <div className="flex-1 min-w-0 flex-col border-b border-theme-border pb-6">
            {breadcrumb}
            <div className="w-full mb-5">
              {renderLowerThird(true)}
              {lead}
            </div>
            <BannerRow
              initialBannerAd={props.initialTopBannerAd}
              initialSquareAd={props.initialTopSquareAd}
              sideClassName="flex w-3/10 justify-center"
              stackBelow={false}
            />
          </div>
        </div>
        <div className="flex gap-10">
          <div className="w-7/10">
            <div className="flex flex-wrap gap-3 mb-6 items-center">
              {scoreRow}
            </div>
            <div className="flex flex-col">
              <div className="w-full flex flex-col gap-6">
                <div
                  className={`article-body w-full flex-1 ${
                    article.contentBody.includes("root") ? "editor-shell" : ""
                  }`}
                >
                  {article.contentBody.includes("root") ? (
                    <ContentBodyLexical contentBody={article.contentBody} />
                  ) : article.contentBody ? (
                    <ContentBodyHtml contentBody={article.contentBody} imageDimensions={props.bodyImageDimensions} />
                  ) : null}
                </div>

                {article.imageGallery && article.imageGallery.length > 0 ? (
                  <ImageGallery
                    imageData={article.imageGallery}
                    currentIndex={currentIndexLaptop}
                    setCurrentIndex={setCurrentIndexLaptop}
                    type="Laptop"
                  ></ImageGallery>
                ) : (
                  ""
                )}
                {footerActions}
                {tagsSection}
                <div className="mb-6">
                  <div className="w-full max-w-[810px] mx-auto cursor-pointer empty:hidden my-2">
                    <HorizontalAds type="GLOBAL" subLocation="BOTTOM" initialAd={props.initialBottomBannerAd} />
                  </div>
                  {article.game.length > 0 ? (
                    <GameDetailCard
                      id={article.game[0]._id}
                      category={article.category}
                      initialGame={article.game[0]}
                    />
                  ) : (
                    ""
                  )}
                </div>

                {writerSection}
                {gameData.length > 2 && article.game.length > 0 ? (
                  <div className="mb-6">
                    <RecommendedList gameContents={gameData} />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          {/* The 30% column the tablet split already reserves; without it
              tablet was the only width missing the side ads. */}
          <div className="w-3/10 flex flex-col gap-6">
            <VerticalAds type="GLOBAL" subLocation="TOP" />
            <Kosana type="ARTICLE" subLocation="SIDEBOTTOM" ads="double" />
          </div>
        </div>
      </div>
      {/* mobile */}
      <div className="flex flex-col md:hidden px-4">
        <div className="mt-8 flex mb-4">
          <div className="flex-1 min-w-0 flex-col border-b border-theme-border pb-4">
            {breadcrumb}
            <div className="w-full mb-5">
              {renderLowerThird(false)}
              {lead}
              <div className="mt-4 flex flex-wrap items-center gap-1.5">
                {renderHeaderActions()}
              </div>
            </div>
            {/* Mobile layout: the square stays hidden, so this is the plain banner. */}
            <BannerRow
              initialBannerAd={props.initialTopBannerAd}
              initialSquareAd={props.initialTopSquareAd}
              className="mt-4"
            />
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <div className="w-full">
            {(ratingScore || article.game[0]) ? (
            <div className="flex flex-wrap gap-3 mb-4 mt-4 items-center">
              {scoreRow}
            </div>
            ) : null}
            <div className="flex flex-col">
              <div className="w-full flex flex-col gap-6">
                <div
                  className={`article-body w-full flex-1 ${
                    article.contentBody.includes("root") ? "editor-shell" : ""
                  }`}
                >
                  {article.contentBody.includes("root") ? (
                    <ContentBodyLexical contentBody={article.contentBody} />
                  ) : article.contentBody ? (
                    <ContentBodyHtml contentBody={article.contentBody} imageDimensions={props.bodyImageDimensions} />
                  ) : null}
                </div>

                {article.imageGallery && article.imageGallery.length > 0 ? (
                  <ImageGallery
                    imageData={article.imageGallery}
                    currentIndex={currentIndexMobile}
                    setCurrentIndex={setCurrentIndexMobile}
                    type="Mobile"
                  ></ImageGallery>
                ) : (
                  ""
                )}
                {footerActions}
                <div className="flex flex-col gap-2">
                  {tagsSection}
                  <div className="w-full cursor-pointer empty:hidden my-1">
                    <HorizontalAds type="GLOBAL" subLocation="BOTTOM" initialAd={props.initialBottomBannerAd} />
                  </div>
                  <div className="empty:hidden mt-3">
                    <VerticalAds type="GLOBAL" subLocation="TOP"></VerticalAds>
                  </div>
                  {article.game.length > 0 ? (
                    <div className="mt-4">
                      <GameDetailCard
                        id={article.game[0]._id}
                        category={article.category}
                        initialGame={article.game[0]}
                      />
                    </div>
                  ) : (
                    ""
                  )}
                </div>

                {latestGuide.length > 0 ? (
                  <div className="bg-theme-card">
                    {guidesPanelTitle}
                    <div className="flex flex-col">
                      <LatestGuideList guideList={latestGuide} />
                    </div>
                    <div className="md:hidden flex border-t border-theme-border p-4">
                      <Link
                        href={"/game/guides/" + props.gameId}
                        prefetch={false}
                        className="btn-signal w-full">

                          See All Guides

                      </Link>
                    </div>
                  </div>
                ) : null}
                {writerSection}
                {isLogin ? null : (
                  <div className="md:hidden block">
                    {signInPanel}
                  </div>
                )}
                {gameData.length > 2 && article.game.length > 0 ? (
                  <div className="mb-6">
                    <RecommendedList gameContents={gameData} />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* comments — one tree for every breakpoint. The wrapper mirrors the
          widths of the three trees above (px-4 / md:px-8 / lg container with
          a 70% column, less the 28px that column gives up to the gap-10
          beside it, so the comments line up with the body) and is never
          display:none, so the observer on the
          anchor fires at every breakpoint. flex-col keeps the child margins
          from collapsing, exactly as inside the flex trees before. */}
      <div className="flex flex-col px-4 md:px-8 lg:container lg:mx-auto lg:px-12 xl:px-24 lg:mt-6">
        <div className="w-full lg:w-[calc(70%_-_28px)]">
          <div id="comments" ref={commentsRef} className="mb-10 mt-4 scroll-mt-24">
            <Comment contentId={props.articleId} active={commentsActive} />
          </div>
        </div>
        <div className="w-full mb-10 md:hidden">
          <div className="w-full h-full flex justify-center">
            {/* <SmallAds /> */}
            <Kosana
              type="ARTICLE"
              subLocation="SIDEBOTTOM"
              ads="double"
            ></Kosana>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default Article;
